import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import {
  detailActions,
  detailSelectors,
} from 'store/reducers/crawlerReducer/detailSlice';
import { useToast } from 'hooks/useToast';
import * as crawlerApi from 'api/crawlerApi';
import {
  CrawlerDetailFormFields,
  mapCrawlerDetailToFormFields,
  mapFormFieldsToCrawlerDetailEditPayload,
} from '../types/crawlerDetailFormFields';
import {
  mapCrawlerDetailToPastCrawlerDetail,
  PastCrawlerDetail,
} from '../types/pastCrawlerDetail';

export const useEditDetailStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const selectedCrawler = useAppSelector(detailSelectors.selectedCrawler);
  const isOpenModal = useAppSelector(detailSelectors.isOpenModalEditDetail);
  const crawlerDetails = useAppSelector(detailSelectors.selectedCrawlerDetails);
  const [pastCrawlerDetails, setPastCrawlerDetails] = useState<
    PastCrawlerDetail[]
  >([]);

  useEffect(() => {
    const [_, ...rest] = crawlerDetails;
    setPastCrawlerDetails(rest.map(mapCrawlerDetailToPastCrawlerDetail));
  }, [crawlerDetails]);

  const form = useForm<CrawlerDetailFormFields>({
    mode: 'all',
  });
  const { getValues, reset } = form;

  useEffect(() => {
    if (crawlerDetails.length > 0) {
      const [crawlerDetail] = crawlerDetails;
      reset(mapCrawlerDetailToFormFields(crawlerDetail));
    }
  }, [crawlerDetails, reset]);

  const onCloseModal = useCallback(() => {
    dispatch(detailActions.updateIsOpenModalEditDetail(false));
  }, [dispatch]);

  useEffect(() => {
    if (selectedCrawler) {
      const requestFetch = async () => {
        dispatch(detailActions.updateLoadingStatus(true));
        const result = await crawlerApi.fetchCrawlerDetails(selectedCrawler.id);
        if (result.ok) {
          dispatch(detailActions.updateSelectedDetails(result.value));
        } else {
          toast({
            status: 'error',
            description: `クローラーバージョン履歴の取得に失敗しました。`,
          });
        }
        dispatch(detailActions.updateLoadingStatus(false));
      };

      requestFetch().catch(() => {
        toast({
          status: 'error',
          description: `クローラーバージョン履歴の取得に失敗しました。`,
        });
      });
    }
  }, [selectedCrawler, dispatch, toast]);

  const onEditCrawlerDetail = useCallback(() => {
    if (selectedCrawler) {
      const requestEdit = async () => {
        dispatch(loaderActions.setLoading(true));
        dispatch(detailActions.updateLoadingStatus(true));
        const values = getValues();
        const result = await crawlerApi.editCrawlerDetail(
          selectedCrawler.id,
          values.version,
          mapFormFieldsToCrawlerDetailEditPayload(values),
        );
        if (result.ok) {
          toast({
            status: 'success',
            description: `バージョンを更新しました。`,
          });

          onCloseModal();
        } else {
          toast({
            status: 'error',
            description: `バージョンの更新に失敗しました。`,
          });
        }
        dispatch(detailActions.updateLoadingStatus(false));
        dispatch(loaderActions.setLoading(false));
      };

      requestEdit().catch(() => {
        toast({
          status: 'error',
          description: `バージョンの更新に失敗しました。`,
        });
      });
    }
  }, [selectedCrawler, toast, dispatch, onCloseModal, getValues]);

  const onOpenModalEditDetail = useCallback(() => {
    dispatch(detailActions.updateIsOpenModalEditDetail(true));
  }, [dispatch]);

  const onOpenModalAddDetail = useCallback(() => {
    dispatch(detailActions.updateIsOpenModalAddDetail(true));
  }, [dispatch]);

  const onOpenModalDeleteDetail = useCallback(() => {
    dispatch(detailActions.updateIsOpenModalDeleteDetail(true));
  }, [dispatch]);

  return {
    ...form,
    /** クローラーバージョン履歴 */
    crawlerDetails,
    /** クローラーバージョン履歴（過去分） */
    pastCrawlerDetails,
    /** クローラーバージョンの入力内容 */
    values: getValues(),
    /** クローラーバージョン履歴の更新キャンセル */
    onCloseModal,
    /** クローラーバージョン履歴の確認モーダルの表示状態 */
    isOpenModal,
    /** クローラーバージョン更新 */
    onEditCrawlerDetail,
    /** クローラーバージョン追加のモーダルを表示 */
    onOpenModalAddDetail,
    /** クローラーバージョンの更新モーダルを表示 */
    onOpenModalEditDetail,
    /** クローラーバージョンの削除確認モーダルを表示 */
    onOpenModalDeleteDetail,
  };
};
